import { BrandLogo } from '../../components/BrandLogo';

export default function MaintenancePage() {
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div
      data-testid="maintenance-page"
      className="relative isolate flex min-h-screen flex-col items-center justify-between px-6 py-12 text-center overflow-hidden bg-[#07080a] text-text-primary select-none"
    >
      {/* Ambient Steel-Cyan Radial Bloom */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 -z-10 h-[520px] w-[900px] max-w-none rounded-full bg-gradient-to-b from-cyan-500/15 via-blue-600/10 to-transparent blur-3xl"
      />

      {/* Hero Background Image & Atmospheric Vignette Overlays */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-20 overflow-hidden">
        <img
          data-testid="maintenance-bg-image"
          src="/images/hero_background.jpg"
          alt=""
          className="h-full w-full object-cover object-center opacity-40 md:opacity-50"
          loading="eager"
        />
        {/* Vignette Gradients for Text Contrast & Seamless Surface Blending */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#07080a] via-transparent to-[#07080a]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#07080a]/75 via-transparent to-[#07080a]/75" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(7,8,10,0.65)_0%,_transparent_75%)]" />
      </div>

      {/* Top Header with Brand Logo */}
      <header className="w-full max-w-5xl flex justify-center py-4">
        <BrandLogo size="lg" />
      </header>

      {/* Central Card / Notification */}
      <main className="flex flex-col items-center justify-center max-w-2xl my-auto py-8">
        {/* Status Badge */}
        <div
          role="status"
          aria-label="Maintenance in progress"
          className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full text-xs font-semibold tracking-wider uppercase border border-amber-500/30 bg-amber-500/10 text-amber-300 mb-6"
        >
          <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" aria-hidden="true" />
          Maintenance Mode
        </div>

        {/* Main Headline */}
        <h1 className="text-[32px] sm:text-[44px] md:text-[52px] font-semibold tracking-[-0.03em] leading-[1.15] max-w-xl">
          <span className="text-steel-shimmer block pb-1">Platform Maintenance.</span>
          <span className="bg-gradient-to-r from-sky-300 via-cyan-400 to-blue-500 bg-clip-text text-transparent block">
            We will be back shortly.
          </span>
        </h1>

        {/* Sub-headline explanation */}
        <p className="mt-5 text-[15px] sm:text-[17px] text-slate-300/85 max-w-lg leading-[1.6] tracking-[-0.003em]">
          TradeFluency is currently undergoing scheduled maintenance and platform upgrades. All public services and the client dashboard are temporarily inaccessible.
        </p>

        {/* Call to Actions */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3.5 w-full sm:w-auto">
          <button
            type="button"
            onClick={handleRefresh}
            className="group relative inline-flex items-center justify-center px-7 py-3 text-[14px] font-semibold rounded-xl bg-gradient-to-r from-sky-500 via-blue-600 to-blue-600 text-white border border-cyan-400/50 shadow-[0_0_20px_rgba(56,189,248,0.35)] hover:from-sky-400 hover:via-blue-500 hover:to-blue-500 hover:border-cyan-400 hover:shadow-[0_0_32px_rgba(56,189,248,0.7)] transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#07080a] min-w-[140px] cursor-pointer"
          >
            <svg
              className="mr-2 h-4 w-4 transition-transform group-hover:rotate-180 duration-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            <span>Check Again</span>
          </button>

          <a
            href="mailto:support@trade-fluency.com"
            className="inline-flex items-center justify-center px-7 py-3 text-[14px] font-semibold rounded-xl border border-slate-700/90 text-slate-200 bg-transparent hover:bg-slate-800/80 hover:text-white hover:border-cyan-400/50 hover:shadow-[0_0_16px_rgba(56,189,248,0.2)] transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#07080a] min-w-[140px]"
          >
            Contact Support
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full max-w-5xl py-4 text-xs text-slate-500 flex flex-col sm:flex-row items-center justify-between gap-2 border-t border-slate-800/60">
        <p>&copy; {new Date().getFullYear()} TradeFluency. All rights reserved.</p>
        <p className="text-slate-500 text-[11.5px]">Institutional Forex Precision &bull; Multi-Broker Execution</p>
      </footer>
    </div>
  );
}
